
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import TherapistInfoDialog from "./home/TherapistInfoDialog";
import { Checkbox } from "@/components/ui/checkbox";
import TermsAndConditionsDialog from "./auth/TermsAndConditionsDialog";

interface RoleSelectorProps {
  userId: string;
  onComplete?: () => void;
}

const RoleSelector = ({ userId, onComplete }: RoleSelectorProps) => {
  const [selectedRole, setSelectedRole] = useState<string>("client");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTherapistDialog, setShowTherapistDialog] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!termsAccepted) {
      // Just show terms dialog without requiring scroll to bottom
      setShowTermsDialog(true);
      return;
    }
    
    // If therapist is selected, show the therapist info dialog instead of submitting
    if (selectedRole === 'therapist') {
      setShowTherapistDialog(true);
      return;
    }
    
    await completeRegistration();
  };

  const completeRegistration = async () => {
    setIsSubmitting(true);

    try {
      console.log("Attempting to register user:", userId, "Role:", selectedRole);
      
      // First check if the user already has this role
      const { data: existingRole, error: checkError } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId)
        .eq('role', selectedRole as any)
        .maybeSingle();
      
      if (checkError) {
        console.error("Error checking for existing role:", checkError);
        throw checkError;
      }
      
      // Only insert if the role doesn't already exist
      if (!existingRole) {
        console.log("No existing role found, inserting new role");
        try {
          // Insert into user_roles table
          const { error } = await supabase
            .from('user_roles')
            .insert({
              user_id: userId,
              role: selectedRole as any, 
            });

          if (error) {
            if (error.code === '23505') {
              // Duplicate key error, role might have been added by another process
              console.log("Role already exists (from error handler)");
            } else {
              console.error("Error setting role:", error);
              throw error;
            }
          }
        } catch (insertError: any) {
          if (insertError.code === '23505') {
            // Handle duplicate key error gracefully
            console.log("Role already exists (from catch)");
          } else {
            throw insertError;
          }
        }
      } else {
        console.log("Role already exists, skipping insert");
      }

      // Update user_profiles table with terms acceptance
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          terms_accepted: true,
          terms_accepted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (profileError) {
        console.error("Error updating terms acceptance:", profileError);
        // Continue despite profile update error
      }

      toast.success(`You have successfully registered as a ${selectedRole}`);
      
      // Immediately navigate based on role
      if (selectedRole === 'client') {
        console.log("Navigating to journal page");
        setTimeout(() => navigate('/journal'), 500);
      } else if (selectedRole === 'therapist') {
        console.log("Navigating to therapist approval page");
        setTimeout(() => navigate('/therapist-approval'), 500);
      }
      
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error("Full error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to set role. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTermsAccept = async () => {
    setTermsAccepted(true);
    setShowTermsDialog(false);
    
    // Auto-submit after accepting terms
    if (selectedRole === 'therapist') {
      setShowTherapistDialog(true);
    } else {
      await completeRegistration();
    }
  };

  const handleTherapistDialogClose = async (proceed: boolean) => {
    setShowTherapistDialog(false);
    
    if (proceed) {
      // Proceed with therapist registration
      setSelectedRole('therapist');
      await completeRegistration();
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Select your role</h3>
          <RadioGroup
            value={selectedRole}
            onValueChange={setSelectedRole}
            className="space-y-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="client" id="client" />
              <Label htmlFor="client">Client</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="therapist" id="therapist" />
              <Label htmlFor="therapist">Therapist</Label>
            </div>
          </RadioGroup>
          
          {selectedRole === "therapist" && (
            <p className="text-amber-600 text-sm">
              Note: Therapist registration is by invitation only.
            </p>
          )}
        </div>

        <div className="flex items-center space-x-2 mt-4">
          <Checkbox 
            id="terms" 
            checked={termsAccepted} 
            onCheckedChange={(checked) => {
              setTermsAccepted(!!checked);
            }} 
          />
          <label
            htmlFor="terms"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
          >
            I accept the <span 
              className="text-[#3BAFDA] hover:text-[#2E9AC4] underline cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                setShowTermsDialog(true);
              }}
            >
              Terms and Conditions
            </span>
          </label>
        </div>
        {/* Add clarifying text */}
        <p className="text-xs text-[#5A636E] mt-1 ml-6">
          By checking this box, you confirm that you have read and agree to our Terms and Conditions.
        </p>

        <Button type="submit" disabled={isSubmitting || !termsAccepted} className="w-full">
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            "Continue"
          )}
        </Button>
      </form>
      
      <TherapistInfoDialog 
        open={showTherapistDialog} 
        onOpenChange={setShowTherapistDialog}
        onContinue={() => handleTherapistDialogClose(true)} 
      />

      <TermsAndConditionsDialog
        open={showTermsDialog}
        onOpenChange={setShowTermsDialog}
        onAccept={handleTermsAccept}
        userType={selectedRole as "client" | "therapist"}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default RoleSelector;
